import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { ivaLabel } from "./voucherIva";

const HEADER_STYLE = {
	fill: { fgColor: { rgb: "253451" } },
	font: { bold: true, color: { rgb: "FFFFFF" } },
	alignment: { vertical: "center", horizontal: "center", wrapText: true },
};

const TITLE_STYLE = {
	fill: { fgColor: { rgb: "E8EEF8" } },
	font: { bold: true, color: { rgb: "253451" }, sz: 16 },
	alignment: { vertical: "center" },
};

const WARNING_STYLE = {
	fill: { fgColor: { rgb: "FFF3CD" } },
	font: { bold: true, color: { rgb: "7C2D12" } },
	alignment: { vertical: "top", wrapText: true },
};

const MONEY_FORMAT = '"$" #,##0.00';

const toNumber = (value) => Number(value || 0);

const toDate = (value, dateOnly = false) => {
	if (!value) return null;
	const normalized = String(value);
	const parsed = dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(normalized)
		? dayjs(`${normalized}T00:00:00`)
		: dayjs(value);
	return parsed.isValid() ? parsed.toDate() : null;
};

const clientName = (cliente) => cliente?.razonSocial
	|| [cliente?.apellido, cliente?.nombre].filter(Boolean).join(", ")
	|| "-";

const invoiceUserName = (usuario) => [usuario?.nombre, usuario?.apellido].filter(Boolean).join(" ") || "-";

const joinConsumptionValues = (consumos, field) => (consumos || [])
	.map((consumo) => consumo?.[field])
	.filter((value) => value !== null && value !== undefined && value !== "")
	.join(", ");

const applyStyle = (sheet, range, style) => {
	const decoded = XLSX.utils.decode_range(range);
	for (let row = decoded.s.r; row <= decoded.e.r; row += 1) {
		for (let column = decoded.s.c; column <= decoded.e.c; column += 1) {
			const address = XLSX.utils.encode_cell({ r: row, c: column });
			if (sheet[address]) sheet[address].s = style;
		}
	}
};

const setColumnFormat = (sheet, column, startRow, endRow, format) => {
	for (let row = startRow; row <= endRow; row += 1) {
		const address = `${column}${row}`;
		if (sheet[address]) sheet[address].z = format;
	}
};

const buildSummarySheet = (detalle, facturaEmitida) => {
	const resumen = detalle?.resumen || {};
	const saldoFacturacion = toNumber(resumen.saldoFacturacion ?? resumen.pendienteFacturar);
	const saldoLabel = saldoFacturacion > 0
		? "Pendiente de facturar"
		: saldoFacturacion < 0 ? "Saldo a favor" : "Saldado";
	const issuedInvoiceRows = facturaEmitida ? [
		[],
		["Factura recién emitida", facturaEmitida.numeroDeFacturaAfip || facturaEmitida.id || "-"],
		["Tipo de factura", facturaEmitida.tipoFactura || "-"],
		["Actividad / IVA", [facturaEmitida.actividadCodigo, ivaLabel(facturaEmitida.tratamientoIva, facturaEmitida.alicuotaIvaId)].filter(Boolean).join(" · ") || "-"],
		["Importe emitido", toNumber(facturaEmitida.total)],
		["CAE", facturaEmitida.CAE || "-"],
	] : [];
	const rows = [
		["Detalle de facturación de vouchers", ""],
		[],
		["Grupo", detalle?.nombreGrupo || "-"],
		["Sede", detalle?.nombreSede || "-"],
		["Generado", toDate(detalle?.generadoEn)],
		[],
		["Total en moneda - aplicado en vouchers", toNumber(resumen.totalAplicado)],
		["Total facturado", toNumber(resumen.totalFacturado)],
		[saldoLabel, Math.abs(saldoFacturacion)],
		...issuedInvoiceRows,
	];
	const sheet = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
	sheet["!merges"] = [XLSX.utils.decode_range("A1:B1")];
	sheet["!cols"] = [{ wch: 31 }, { wch: 32 }];
	sheet["!rows"] = [{ hpt: 25 }];
	sheet["!freeze"] = { xSplit: 0, ySplit: 2 };
	applyStyle(sheet, "A1:B1", TITLE_STYLE);
	setColumnFormat(sheet, "B", 7, 9, MONEY_FORMAT);
	if (facturaEmitida) setColumnFormat(sheet, "B", 13, 13, MONEY_FORMAT);
	setColumnFormat(sheet, "B", 5, 5, "dd/mm/yyyy hh:mm");
	return sheet;
};

const buildControlSheet = (detalle, facturaEmitida) => {
	const control = detalle?.controlHistorico;
	if (!control) return null;
	const saldoDespues = facturaEmitida?.saldoDespuesFactura ?? detalle?.resumen?.saldoFacturacion ?? 0;
	const rows = [
		["Control de reconstrucción histórica", ""],
		[],
		["Estado", control.reconstruccionExacta ? "Reconstrucción exacta" : "Reconstrucción con advertencias"],
		["Advertencia", control.advertencia || "Sin diferencias detectadas"],
		["Corte anterior", toDate(control.fechaCorteDesde)],
		["Corte de la factura", toDate(control.fechaCorteHasta)],
		["Saldo arrastrado", toNumber(control.saldoArrastrado)],
		["Descuentos de pedidos del período", toNumber(control.descuentosPeriodo)],
		["Facturas intermedias", toNumber(control.facturasIntermedias)],
		["Saldo esperado antes de facturar", toNumber(control.saldoEsperadoAntes)],
		["Saldo registrado antes de facturar", toNumber(control.saldoRegistradoAntes)],
		["Diferencia de control", toNumber(control.diferenciaControl)],
		[],
		["Importe de la factura", toNumber(facturaEmitida?.total)],
		["Saldo después de facturar", toNumber(saldoDespues)],
		["Fuente del saldo anterior", control.fuenteSaldoAnterior || "-"],
		["Pedidos sin fecha de cierre", toNumber(control.pedidosSinFechaCierre)],
	];
	const sheet = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
	sheet["!merges"] = [XLSX.utils.decode_range("A1:B1")];
	sheet["!cols"] = [{ wch: 38 }, { wch: 90 }];
	sheet["!rows"] = [{ hpt: 25 }, undefined, undefined, { hpt: 60 }];
	sheet["!freeze"] = { xSplit: 0, ySplit: 2 };
	applyStyle(sheet, "A1:B1", TITLE_STYLE);
	if (control.advertencia) applyStyle(sheet, "A3:B4", WARNING_STYLE);
	setColumnFormat(sheet, "B", 5, 6, "dd/mm/yyyy hh:mm");
	setColumnFormat(sheet, "B", 7, 15, MONEY_FORMAT);
	return sheet;
};

const buildOrdersSheet = (detalle) => {
	const pedidos = detalle?.pedidos || [];
	const includeClosingDate = Boolean(detalle?.controlHistorico)
		|| pedidos.some((pedido) => pedido?.fechaCierrePedido);
	const headers = [
		"ID pedido",
		"Fecha",
		"Estado",
		"Cliente",
		"DNI",
		"Total pedido",
		"Cubierto por voucher",
		"Remanente del cliente",
		"Voucher",
		"Tipos voucher",
		"Cantidades utilizadas",
		...(includeClosingDate ? ["Fecha cierre pedido"] : []),
	];
	const rows = pedidos.map((pedido) => [
		String(pedido?.idPedido || ""),
		toDate(pedido?.fechaPedido, true),
		pedido?.estadoPedido || "-",
		clientName(pedido?.cliente),
		pedido?.cliente?.dni ? String(pedido.cliente.dni) : "-",
		toNumber(pedido?.totalPedido),
		toNumber((pedido?.consumos || []).reduce((sum, consumo) => sum + toNumber(consumo?.montoDescuento), 0)),
		toNumber((pedido?.consumos || []).reduce((sum, consumo) => sum + toNumber(consumo?.totalVenta), 0)),
		detalle?.nombreGrupo || "-",
		joinConsumptionValues(pedido?.consumos, "tipoVoucher"),
		joinConsumptionValues(pedido?.consumos, "cantidadUtilizada"),
		...(includeClosingDate ? [toDate(pedido?.fechaCierrePedido)] : []),
	]);
	const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows], { cellDates: true });
	sheet["!cols"] = [
		{ wch: 14 }, { wch: 12 }, { wch: 13 }, { wch: 30 }, { wch: 15 },
		{ wch: 17 }, { wch: 21 }, { wch: 21 }, { wch: 30 }, { wch: 20 }, { wch: 24 },
		...(includeClosingDate ? [{ wch: 21 }] : []),
	];
	const lastColumn = includeClosingDate ? "L" : "K";
	sheet["!autofilter"] = { ref: `A1:${lastColumn}${Math.max(rows.length + 1, 1)}` };
	sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
	applyStyle(sheet, `A1:${lastColumn}1`, HEADER_STYLE);
	setColumnFormat(sheet, "B", 2, rows.length + 1, "dd/mm/yyyy");
	setColumnFormat(sheet, "F", 2, rows.length + 1, MONEY_FORMAT);
	setColumnFormat(sheet, "G", 2, rows.length + 1, MONEY_FORMAT);
	setColumnFormat(sheet, "H", 2, rows.length + 1, MONEY_FORMAT);
	if (includeClosingDate) setColumnFormat(sheet, "L", 2, rows.length + 1, "dd/mm/yyyy hh:mm");
	return sheet;
};

const buildInvoicesSheet = (detalle) => {
	const headers = [
		"Fecha",
		"Tipo",
		"Estado",
		"Número ARCA",
		"Cliente facturado",
		"CUIT",
		"Total",
		"CAE",
		"Vencimiento CAE",
		"Usuario emisor",
		"Concepto",
		"Error",
	];
	const rows = (detalle?.facturas || []).map((factura) => {
		const snapshot = factura?.clienteSnapshot || factura?.clienteFacturacion || {};
		return [
			toDate(factura?.createdAt || factura?.fechaFactura),
			factura?.tipoFactura || "-",
			factura?.estado || "-",
			factura?.numeroDeFacturaAfip ? String(factura.numeroDeFacturaAfip) : "-",
			factura?.clienteFacturado || clientName(snapshot),
			factura?.cuitClienteFacturado || (snapshot?.cuit ? String(snapshot.cuit) : "-"),
			toNumber(factura?.total),
			factura?.CAE ? String(factura.CAE) : "-",
			toDate(factura?.CAEFchVto, true),
			invoiceUserName(factura?.usuarioFactura),
			factura?.conceptoTexto || "-",
			factura?.error || "",
		];
	});
	const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows], { cellDates: true });
	sheet["!cols"] = [
		{ wch: 18 }, { wch: 9 }, { wch: 13 }, { wch: 18 }, { wch: 30 },
		{ wch: 16 }, { wch: 17 }, { wch: 18 }, { wch: 18 }, { wch: 24 },
		{ wch: 52 }, { wch: 45 },
	];
	sheet["!autofilter"] = { ref: `A1:L${Math.max(rows.length + 1, 1)}` };
	sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
	applyStyle(sheet, "A1:L1", HEADER_STYLE);
	setColumnFormat(sheet, "A", 2, rows.length + 1, "dd/mm/yyyy hh:mm");
	setColumnFormat(sheet, "G", 2, rows.length + 1, MONEY_FORMAT);
	setColumnFormat(sheet, "I", 2, rows.length + 1, "dd/mm/yyyy");
	return sheet;
};

export const buildVoucherFacturacionWorkbook = (detalle, facturaEmitida) => {
	if (!detalle?.resumen) throw Error("La respuesta no contiene el detalle de facturación");
	const workbook = XLSX.utils.book_new();
	if (detalle.controlHistorico?.advertencia) {
		XLSX.utils.book_append_sheet(
			workbook,
			buildControlSheet(detalle, facturaEmitida),
			"ADVERTENCIA"
		);
	}
	XLSX.utils.book_append_sheet(workbook, buildSummarySheet(detalle, facturaEmitida), "Resumen");
	XLSX.utils.book_append_sheet(workbook, buildOrdersSheet(detalle), "Pedidos");
	XLSX.utils.book_append_sheet(workbook, buildInvoicesSheet(detalle), "Facturas");
	workbook.Workbook = workbook.Workbook || {};
	workbook.Workbook.CalcPr = { calcMode: "auto" };
	return workbook;
};

const sanitizeFileName = (value) => String(value || "grupo")
	.trim()
	.replace(/[<>:"/\\|?*]+/g, "-")
	.replace(/\s+/g, "_")
	.slice(0, 80);

export const downloadVoucherFacturacionExcel = (detalle, facturaEmitida) => {
	const workbook = buildVoucherFacturacionWorkbook(detalle, facturaEmitida);
	const groupName = sanitizeFileName(detalle?.nombreGrupo);
	const timestamp = dayjs(detalle?.generadoEn || new Date()).format("YYYY-MM-DD_HH-mm-ss");
	const fileName = `detalle_facturacion_vouchers_${groupName}_${timestamp}.xlsx`;
	XLSX.writeFile(workbook, fileName, { cellDates: true, cellStyles: true, compression: true });
	return fileName;
};
